<template>
  <v-row>
    <v-col cols="12">
      <v-card class="cn-card" flat>
        <div class="cn-header pl-base-bg">
          <v-icon color="#fff" size="18" class="mr-3">mdi-file-document-minus-outline</v-icon>
          <div>
            <div class="cn-title">Edit Credit Note</div>
            <div class="cn-subtitle">{{ form.creditNoteNumber ? `Credit Note #${form.creditNoteNumber}` : "Update credit note details" }}</div>
          </div>
          <v-spacer />
          <v-chip :color="statusColor(status)" dark small class="text-uppercase font-weight-bold">
            {{ status }}
          </v-chip>
        </div>

        <v-alert v-if="refundRecorded" type="info" outlined dense border="left" class="mx-6 mt-4">
          This credit note's refund has already been recorded and can no longer be edited.
        </v-alert>

        <v-form ref="form" v-model="valid" class="pa-6" :disabled="isLocked">
          <div class="cn-section-label">Credit Note Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.creditNoteNumber"
                label="Credit Note Number"
                outlined
                dense
                :disabled="isLocked"
                :rules="[rules.required, rules.uniqueCreditNote]"
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.creditNoteDate"
                    label="Credit Note Date"
                    outlined
                    dense
                    readonly
                    :disabled="isLocked"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.creditNoteDate" @input="dateMenu = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="4">
              <v-select
                v-model="form.reason"
                :items="reasonOptions"
                label="Reason"
                outlined
                dense
                :disabled="isLocked"
              />
            </v-col>
          </v-row>

          <div class="cn-invoice-summary">
            <div class="cn-invoice-summary-row">
              <span>Against Invoice</span>
              <span>#{{ invoiceNumber }}</span>
            </div>
            <div class="cn-invoice-summary-row">
              <span>Customer</span>
              <span>{{ customerName }}</span>
            </div>
          </div>

          <div class="cn-section-label mt-4">Items</div>

          <div
            v-for="(item, index) in form.items"
            :key="index"
            class="cn-item-row"
          >
            <div class="cn-item-row-head">
              <span class="cn-item-index">Item {{ index + 1 }}</span>
              <v-btn icon x-small class="cn-item-remove" :disabled="isLocked" @click="removeItem(index)">
                <v-icon color="error" size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="item.product"
                  label="Product / Description"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="6" sm="2">
                <v-text-field v-model="item.hsn" label="HSN/SAC" dense outlined hide-details :disabled="isLocked" />
              </v-col>

              <v-col cols="4" sm="1">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Qty"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.price"
                  type="number"
                  label="Rate"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.taxPercent"
                  type="number"
                  label="Tax %"
                  dense
                  outlined
                  hide-details
                  suffix="%"
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>
            </v-row>

            <div class="cn-item-row-foot">
              <span class="cn-item-amount-label">Amount</span>
              <span class="cn-item-amount-value">{{ formatAmount(item.amount) }}</span>
            </div>
          </div>

          <v-btn small class="cn-add-item-btn" :disabled="isLocked" @click="addItem">
            <v-icon left size="16">mdi-plus</v-icon>
            Add Item
          </v-btn>

          <v-divider class="my-4" />
          <v-row dense justify="end">
            <v-col cols="12" sm="4">
              <div class="cn-total-row">
                <span>Subtotal</span>
                <span>{{ formatAmount(subtotal) }}</span>
              </div>
              <div class="cn-total-row">
                <span>GST</span>
                <span>{{ formatAmount(totalGst) }}</span>
              </div>
              <div class="cn-total-row cn-total-row--grand">
                <span>Total</span>
                <span>{{ formatAmount(subtotal + totalGst) }}</span>
              </div>
            </v-col>
          </v-row>

          <v-alert v-if="!isLocked && isZeroAmount" type="warning" outlined dense class="mt-2">
            Credit note total must be greater than ₹0 — enter a quantity and rate for at least one item.
          </v-alert>

          <v-textarea v-model="form.note" label="Note" outlined dense rows="2" :disabled="isLocked" />

          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block color="error" outlined :disabled="isLocked" @click="confirmDelete = true">
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="fetchCreditNote">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block class="cn-save-btn" :loading="loading" :disabled="isLocked || isZeroAmount" @click="submit">
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <!-- DELETE -->
    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="deleteDialogButtons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this credit note?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "EditCreditNote",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      dateMenu: false,
      status: "ISSUED",
      invoiceNumber: "",
      customerName: "",
      transactionId: null,
      confirmDelete: false,
      deleting: false,

      reasonOptions: [
        { text: "Sales Return", value: "SALES_RETURN" },
        { text: "Post-Sale Discount", value: "POST_SALE_DISCOUNT" },
        { text: "Billing Correction", value: "BILLING_CORRECTION" },
        { text: "Other", value: "OTHER" },
      ],

      form: {
        creditNoteNumber: "",
        creditNoteDate: "",
        reason: "SALES_RETURN",
        note: "",
        items: [],
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueCreditNote: (v) => {
          if (!v || !this.creditNotes.length) return true;
          const id = this.$route.params.id;
          return (
            !this.creditNotes.some(
              (c) => c._id !== id && c.creditNoteNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Credit note number already exists"
          );
        },
      },
    };
  },

  created() {
    this.$store.dispatch("creditNotes/getCreditNotes");
    this.fetchCreditNote();
  },

  computed: {
    isCancelled() {
      return this.status === "CANCELLED";
    },

    refundRecorded() {
      return !!this.transactionId;
    },

    isLocked() {
      return this.isCancelled || this.refundRecorded;
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    totalGst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Number(it.taxPercent) || 0;
        return sum + (amount * percent) / 100;
      }, 0);
    },

    // A ₹0 note reduces nothing and refunds nothing — not a
    // representable real-world correction, just noise in the list.
    isZeroAmount() {
      return this.subtotal + this.totalGst <= 0.01;
    },

    creditNotes() {
      return this.$store.state.creditNotes.list || [];
    },

    deleteDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Delete",
          action: this.deleteCreditNote,
          color: "error",
          loading: this.deleting,
        },
      ];
    },
  },

  methods: {
    async fetchCreditNote() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("creditNotes/getCreditNoteById", id);
        const cn = res.creditNote;

        this.status = cn.status;
        this.invoiceNumber = cn.invoiceNumber;
        this.customerName = cn.customer?.name || "-";
        this.transactionId = cn.transactionId || null;

        this.form = {
          creditNoteNumber: cn.creditNoteNumber || "",
          creditNoteDate: cn.creditNoteDate ? new Date(cn.creditNoteDate).toISOString().slice(0, 10) : "",
          reason: cn.reason || "SALES_RETURN",
          note: cn.note || "",
          items: (cn.items || []).map((it) => ({ ...it })),
        };
      } catch (err) {
        this.$toast?.error("Failed to load credit note");
      }
    },

    addItem() {
      this.form.items.push({
        product: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    statusColor(status) {
      switch (status) {
        case "ISSUED":
          return "green";
        case "CANCELLED":
          return "red";
        default:
          return "grey";
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const id = this.$route.params.id;
        const payload = {
          id,
          creditNoteNumber: this.form.creditNoteNumber,
          creditNoteDate: this.form.creditNoteDate,
          reason: this.form.reason,
          items: this.form.items,
          note: this.form.note,
        };

        await this.$store.dispatch("creditNotes/updateCreditNote", payload);

        this.$toast?.success("Credit note updated successfully");
        this.$router.push(`/credit-notes/${id}/view`);
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update credit note",
        );
      } finally {
        this.loading = false;
      }
    },

    async deleteCreditNote() {
      this.deleting = true;
      try {
        await this.$store.dispatch("creditNotes/deleteCreditNote", this.$route.params.id);
        this.$toast?.success("Credit note deleted");
        this.$router.push("/credit-notes");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete credit note",
        );
      } finally {
        this.deleting = false;
        this.confirmDelete = false;
      }
    },
  },
};
</script>

<style scoped>
.cn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.cn-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
}

.cn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.cn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.cn-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.cn-invoice-summary {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 16px;
  margin-bottom: 4px;
}

.cn-invoice-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 13.5px;
  color: #475569;
  padding: 3px 0;
}

.cn-item-row {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
}

.cn-item-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cn-item-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.cn-item-remove {
  margin: -4px -4px 0 0;
}

.cn-item-row-foot {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.cn-item-amount-label {
  font-size: 12px;
  color: #64748b;
}

.cn-item-amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.cn-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.cn-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
  color: #475569;
}

.cn-total-row--grand {
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  margin-top: 4px;
  padding-top: 8px;
}

.cn-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
